import { AssistantRuntimeProvider, useAssistantTransportRuntime } from '@assistant-ui/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { GraphAction } from '@/components/langgraph-chat/types'
import { DeepResearchToolUI } from '@/features/agent/tools/DeepResearchToolUI'
import { EntityResolutionToolUI } from '@/features/agent/tools/EntityResolutionToolUI'
import { FindEntitiesNearLocationToolUI } from '@/features/agent/tools/FindEntitiesNearLocationToolUI'
import { FindEntityByNameToolUI } from '@/features/agent/tools/FindEntityByNameToolUI'
import { FindShortestPathToolUI } from '@/features/agent/tools/FindShortestPathToolUI'
import { GetNodeNeighborhoodToolUI } from '@/features/agent/tools/GetNodeNeighborhoodToolUI'
import {
  DescribeGraphSchemaToolUI,
  GetNodeDetailsToolUI,
} from '@/features/agent/tools/HiddenToolUI'
import { HybridSearchToolUI } from '@/features/agent/tools/HybridSearchToolUI'
import { LakeHybridSearchToolUI } from '@/features/agent/tools/LakeHybridSearchToolUI'
import { LakeSearchDocumentsByNameToolUI } from '@/features/agent/tools/LakeSearchDocumentsByNameToolUI'
import { LakeSearchFoldersByNameToolUI } from '@/features/agent/tools/LakeSearchFoldersByNameToolUI'
import { ReadUserGuidePageToolUI } from '@/features/agent/tools/ReadUserGuidePageToolUI'
import {
  GetEntityResolutionMergeStatusToolUI,
  MergeEntityResolutionToolUI,
  PlanEntityResolutionMergeToolUI,
} from '@/features/agent/tools/ResolutionRuleToolUI'
import { RunCypherToolUI } from '@/features/agent/tools/RunCypherToolUI'
import { SearchUserGuideToolUI } from '@/features/agent/tools/SearchUserGuideToolUI'
import { ShowEntitiesAtLocationToolUI } from '@/features/agent/tools/ShowEntitiesAtLocationToolUI'
import {
  CiteStructuredRowsToolUI,
  OpenCaseEvidenceToolUI,
  QueryStructuredTableToolUI,
  SearchStructuredEvidenceToolUI,
} from '@/features/agent/tools/StructuredEvidenceToolUI'
import { type AgentOptions, getAgentTools, updateThreadOptions } from '@/services/api/agent'
import { apiClient } from '@/services/api/client'
import { getLakeAgentTools, updateLakeThreadOptions } from '@/services/api/lakeAgent'
import { AgentOptionsContext, DEFAULT_AGENT_OPTIONS } from './AgentOptionsContext'
import { getSuggestionsForView, getWelcomeTextForView } from './AgentSuggestions'
import { AgentSuggestionsContext } from './AgentSuggestionsContext'
import { buildHistoryRepository, type LangChainMessage, toThreadMessagesSafe } from './aui-adapter'
import { HistoryPaginationProvider } from './HistoryPaginationContext'
import { messageIdFor } from './messageIdentity'
import { type ThreadStatus, ThreadStatusContext } from './ThreadStatusContext'
import { SchemaProposalToolUI } from './tools/SchemaProposalToolUI'
import { classifyTransportFailure } from './transport-failure'
import {
  agentThreadHealthKey,
  agentThreadListsKey,
  useAgentHistory,
  useThreadHealth,
} from './useAgentRuntime'
import {
  lakeAgentThreadsKey,
  lakeThreadHealthKey,
  useLakeAgentHistory,
  useLakeThreadHealth,
} from './useLakeAgentRuntime'

export type AgentScope = 'case' | 'lake'

interface AgentChatHostProps {
  scope?: AgentScope
  /** Required when scope === 'case'. Ignored otherwise. */
  caseId?: string
  /** The thread the conversation lives in. Never optional: a message sent without one makes the
   *  server start a new thread, and the model would not see the messages before it. */
  threadId: string
  currentView?: string
  onGraphAction?: (action: GraphAction) => void
  /** Called by the "Start new thread" CTA in the ThreadStatusBanner when the
   *  thread is too full to continue. Does NOT delete the current thread — the
   *  parent is expected to create a fresh thread and switch to it. */
  onRequestNewThread?: () => void
  children: ReactNode
}

interface AgentState {
  messages: LangChainMessage[]
  graphActions: GraphAction[]
  thread_status?: ThreadStatus | null
  [k: string]: unknown
}

/** The queries a turn or a change of options makes stale: the thread's health, and the lists
 *  that show its title. None for a case conversation whose case is not known. */
function threadQueryKeys(scope: AgentScope, caseId: string | undefined, threadId: string) {
  if (scope === 'lake') {
    return { health: lakeThreadHealthKey(threadId), threads: lakeAgentThreadsKey() }
  }
  if (!caseId) return null
  return { health: agentThreadHealthKey(caseId, threadId), threads: agentThreadListsKey(caseId) }
}

export function AgentChatHost(props: AgentChatHostProps) {
  const scope: AgentScope = props.scope ?? 'case'
  const { caseId, threadId } = props
  // Both hooks are called, as hooks must be; only the one of this scope asks the server.
  const caseHistory = useAgentHistory(caseId ?? '', threadId, {
    enabled: scope === 'case' && !!caseId,
  })
  const lakeHistory = useLakeAgentHistory(threadId, { enabled: scope === 'lake' })
  const { messages, isLoading, hasMore, isFetchingMore, fetchMore } =
    scope === 'lake' ? lakeHistory : caseHistory

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
      </div>
    )
  }

  return (
    /* Key only on threadId — switching conversations resets the runtime,
     * but loading additional history pages on the same thread does NOT
     * remount. The runtime keeps its in-session state.messages while the
     * converter prepends our paginated `historyMessages`. */
    <AgentRuntimeMount
      key={threadId}
      scope={scope}
      caseId={caseId}
      threadId={threadId}
      currentView={props.currentView}
      onGraphAction={props.onGraphAction}
      onRequestNewThread={props.onRequestNewThread}
      historyMessages={messages}
      hasMore={!!hasMore}
      isFetchingMore={isFetchingMore}
      fetchMore={() => void fetchMore()}
    >
      {props.children}
    </AgentRuntimeMount>
  )
}

function AgentRuntimeMount({
  scope,
  caseId,
  threadId,
  currentView,
  onGraphAction,
  onRequestNewThread,
  historyMessages,
  hasMore,
  isFetchingMore,
  fetchMore,
  children,
}: {
  scope: AgentScope
  caseId?: string
  threadId: string
  currentView?: string
  onGraphAction?: (action: GraphAction) => void
  onRequestNewThread?: () => void
  historyMessages: LangChainMessage[]
  hasMore: boolean
  isFetchingMore: boolean
  fetchMore: () => void
  children: ReactNode
}) {
  void onGraphAction

  const { t } = useTranslation()

  // Keep paginated history in a ref so the converter (created once by the
  // runtime hook) always reads the latest list without forcing the runtime
  // to remount. Newly-loaded older pages are merged in front of
  // `state.messages` (which holds this session's streamed messages),
  // deduped by id so a stream-finished message that later appears in the
  // history page doesn't render twice.
  const historyRef = useRef<LangChainMessage[]>(historyMessages)
  historyRef.current = historyMessages

  // Banner status has two sources, layered:
  //   1. `health.status` — persisted, from the scope's /health endpoint.
  //      Reactive via tanstack-query; auto-refetches after each completed turn.
  //   2. `sessionStatus` — set from SSE updates during a running turn.
  //      Takes precedence once set, since it's the most recent signal.
  const caseHealth = useThreadHealth(caseId ?? '', threadId, {
    enabled: scope === 'case' && !!caseId,
  })
  const lakeHealth = useLakeThreadHealth(threadId, { enabled: scope === 'lake' })
  const health = scope === 'case' ? caseHealth.data : lakeHealth.data
  const persistedStatus = health?.status ?? null
  const [sessionStatus, setSessionStatus] = useState<ThreadStatus | null>(null)
  const [sessionStatusReceived, setSessionStatusReceived] = useState(false)
  const [dismissedStatusRef, setDismissedStatusRef] = useState<ThreadStatus | null>(null)
  const lastSeenStatusRef = useRef<ThreadStatus | null>(null)
  const effectiveStatus = sessionStatusReceived ? sessionStatus : persistedStatus
  const dismiss = useCallback(() => setDismissedStatusRef(effectiveStatus), [effectiveStatus])

  const queryClient = useQueryClient()

  // Per-thread agent options (U4): the thread row is the source of truth
  // (served via /health); `pendingOptions` overlays in-flight edits so the
  // panel responds instantly while the PATCH lands.
  const toolsQuery = useQuery({
    queryKey: ['agent-tools', scope],
    queryFn: () => (scope === 'lake' ? getLakeAgentTools() : getAgentTools()),
    staleTime: Number.POSITIVE_INFINITY,
  })
  const [pendingOptions, setPendingOptions] = useState<AgentOptions | null>(null)
  const agentOptions = pendingOptions ?? health?.agent_options ?? DEFAULT_AGENT_OPTIONS
  const updateAgentOptions = useCallback(
    (next: AgentOptions) => {
      setPendingOptions(next)
      const persist =
        scope === 'lake'
          ? updateLakeThreadOptions(threadId, next)
          : caseId
            ? updateThreadOptions(caseId, threadId, next)
            : Promise.resolve(null)
      persist
        .then(async () => {
          const keys = threadQueryKeys(scope, caseId, threadId)
          if (keys) await queryClient.invalidateQueries({ queryKey: keys.health })
          // Drop the overlay only after the refetched health is in the cache —
          // a lingering overlay would mask changes made from another session.
          setPendingOptions(current => (current === next ? null : current))
        })
        .catch(err => {
          console.error('[agent] failed to persist agent options', err)
          toast.error(t('chat.toolsPanel.saveFailed'))
          setPendingOptions(null) // fall back to the server value
        })
    },
    [scope, caseId, threadId, queryClient, t]
  )

  const agentOptionsContextValue = useMemo(
    () => ({
      options: agentOptions,
      availableTools: toolsQuery.data?.tools ?? [],
      updateOptions: updateAgentOptions,
    }),
    [agentOptions, toolsQuery.data, updateAgentOptions]
  )

  const transportApi = scope === 'lake' ? '/api/v1/lake-agent/chat' : '/api/agent/chat'
  const transportBody =
    scope === 'lake'
      ? { current_view: currentView ?? 'lake', threadId }
      : { case_id: caseId, current_view: currentView, threadId }
  const runtime = useAssistantTransportRuntime<AgentState>({
    initialState: { messages: [], graphActions: [] },
    api: transportApi,
    headers: () => apiClient.getAuthHeaders(),
    body: transportBody,
    converter: (state, meta) => {
      const history = historyRef.current
      const historyIds = new Set(
        history.map(m => (m as { id?: string }).id).filter((id): id is string => !!id)
      )
      const sessionIds = new Set<string>()
      const session = (state.messages ?? []).filter(m => {
        const id = (m as { id?: string }).id
        if (id) sessionIds.add(id)
        return !id || !historyIds.has(id)
      })

      // Optimistic bubbles for sends the backend hasn't echoed yet. The bubble
      // carries the same client-minted id the request sends (see
      // prepareSendCommandsRequest), so the moment the echo lands in the
      // session the optimistic copy is dropped by exact id — no text matching.
      const optimistic: LangChainMessage[] = meta.pendingCommands.flatMap(c => {
        if (c.type !== 'add-message' || c.message?.role !== 'user') return []
        const id = messageIdFor(c)
        if (sessionIds.has(id) || historyIds.has(id)) return []
        const text = (c.message.parts ?? [])
          .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
          .map(p => p.text)
          .join('\n')
        if (!text) return []
        return [{ type: 'human' as const, id, content: text }]
      })

      // Only overwrite seeded status when the backend actually emitted one
      // this session. Without the `in` check, the converter runs with
      // `state.thread_status === undefined` on every render and stomps the
      // persisted status we seeded from history.
      if ('thread_status' in state) {
        const incoming = state.thread_status ?? null
        if (incoming !== lastSeenStatusRef.current) {
          lastSeenStatusRef.current = incoming
          queueMicrotask(() => {
            setSessionStatus(incoming)
            setSessionStatusReceived(true)
          })
        }
      }

      return {
        messages: toThreadMessagesSafe([...history, ...session, ...optimistic], meta.isSending),
        isRunning: meta.isSending,
      }
    },
    // The end of a turn. The runtime calls this after every request, however it ended, so a
    // turn that is over within one render — never rendered as sending — is not missed. The
    // health is read again for the context indicator and the persisted banner, the thread
    // lists for the title the server gave the thread from its first message.
    onFinish: () => {
      const keys = threadQueryKeys(scope, caseId, threadId)
      if (!keys) return
      void queryClient.invalidateQueries({ queryKey: keys.health })
      void queryClient.invalidateQueries({ queryKey: keys.threads })
    },
    // Stamp each outgoing user message with its client-minted id so the
    // backend persists OUR id (optimistic bubble, stream echo, checkpoint and
    // archive all share it; re-sends become idempotent server-side).
    prepareSendCommandsRequest: body => ({
      ...body,
      commands: body.commands.map(c =>
        c.type === 'add-message' && c.message?.role === 'user'
          ? { ...c, message: { ...c.message, id: messageIdFor(c) } }
          : c
      ),
    }),
    onError: (error, { updateState }) => {
      const message = error.message || String(error)
      const failure = classifyTransportFailure(error)
      // Busy ≠ broken: another stream holds this thread's lock and is healthy.
      // Surface as a toast instead of a red in-thread error.
      if (failure === 'busy') {
        toast.info(t('chat.threadBusy'))
        return
      }
      // The backend appends its own error marker to state (and the archive)
      // before failing the stream. Only synthesize a fallback message when
      // that marker never arrived — e.g. the network dropped and no state op
      // landed at all. Without the fallback the SSE ErrorChunk is silently
      // swallowed when the runtime resets its command queue.
      updateState(prev => {
        const messages = prev.messages ?? []
        const last = messages[messages.length - 1] as
          | { additional_kwargs?: Record<string, unknown> }
          | undefined
        if (last?.additional_kwargs?.spectre_turn_error) return prev
        const errorMessage: LangChainMessage = {
          id: `error-${crypto.randomUUID()}`,
          type: 'ai',
          // A lost connection is not the assistant's failure: say so, and leave the
          // browser's or the gateway's own text to the error box.
          content:
            failure === 'connection'
              ? t('chat.connectionLost')
              : t('chat.streamError', { message }),
          status: { type: 'incomplete', reason: 'error', error: message },
        }
        return { ...prev, messages: [...messages, errorMessage] }
      })
    },
  })

  const visibleStatus =
    effectiveStatus && effectiveStatus !== dismissedStatusRef ? effectiveStatus : null

  // Pagination is a two-step transaction so the viewport sees
  // `autoScroll={false}` BEFORE `thread.import` rewrites the message
  // list. Without the flip, assistant-ui's anchoring pass runs after
  // our scroll-restore rAF and the user sees a small drift down. With
  // the flip, autoScroll stays off until our restore is done.
  //
  //  step 1 (this effect): detect a new older page, capture scroll
  //                        anchor metrics, stash the import payload,
  //                        flip isPaging=true → re-render with
  //                        autoScroll=false.
  //  step 2 (next effect): once autoScroll has rendered as false, run
  //                        the import, double-rAF, restore scroll,
  //                        flip isPaging=false.
  const [isPaging, setIsPaging] = useState(false)
  const lastOldestIdRef = useRef<string | null>(
    (historyMessages[0] as { id?: string } | undefined)?.id ?? null
  )
  const pendingImportRef = useRef<{
    repo: ReturnType<typeof buildHistoryRepository>
    anchorDomId: string | null
    anchorTop: number | null
    prevScrollTop: number
    prevScrollHeight: number
  } | null>(null)

  useEffect(() => {
    const oldestId = (historyMessages[0] as { id?: string } | undefined)?.id ?? null
    if (oldestId === lastOldestIdRef.current) return
    lastOldestIdRef.current = oldestId
    if (historyMessages.length === 0) return

    // Anchor by READING the DOM rather than guessing the id assistant-ui
    // assigns. assistant-ui rewrites assistant message ids to its own
    // `lc_run--…` run-id form, so the LangChain UUIDs in our state never
    // match `[data-message-id]` on assistant rows — only user rows. We
    // pick the topmost message whose top edge is at or below the
    // viewport's top, and remember its DOM id + offset.
    const viewport = document.querySelector<HTMLElement>('.aui-thread-viewport')
    let anchorDomId: string | null = null
    let anchorTop: number | null = null
    if (viewport) {
      const vTop = viewport.getBoundingClientRect().top
      const rows = viewport.querySelectorAll<HTMLElement>('[data-message-id]')
      for (const row of rows) {
        const top = row.getBoundingClientRect().top
        if (top >= vTop - 2) {
          anchorDomId = row.getAttribute('data-message-id')
          anchorTop = top
          break
        }
      }
      // Fallback: last visible row if none was at-or-below the top edge
      // (e.g. a giant single message that overflows the viewport).
      if (!anchorDomId && rows.length) {
        const last = rows[rows.length - 1]
        anchorDomId = last.getAttribute('data-message-id')
        anchorTop = last.getBoundingClientRect().top
      }
    }

    pendingImportRef.current = {
      repo: buildHistoryRepository(historyMessages),
      anchorDomId,
      anchorTop,
      prevScrollTop: viewport?.scrollTop ?? 0,
      prevScrollHeight: viewport?.scrollHeight ?? 0,
    }
    setIsPaging(true)
  }, [historyMessages])

  useEffect(() => {
    if (!isPaging || !pendingImportRef.current) return
    const { repo, anchorDomId, anchorTop, prevScrollTop, prevScrollHeight } =
      pendingImportRef.current
    pendingImportRef.current = null

    try {
      runtime.thread.import(repo)
    } catch (e) {
      console.error('[agent] history import failed', e)
      setIsPaging(false)
      return
    }

    const viewport = document.querySelector<HTMLElement>('.aui-thread-viewport')
    if (!viewport) {
      setIsPaging(false)
      return
    }

    // assistant-ui's response to `thread.import` lands in multiple
    // commit passes — we observed scrollHeight thrashing 2200 → 2477 →
    // 2489 → 4928 → 3876 → settle. A single rAF restores against a
    // transient height. We re-apply the correction across several
    // frames; the repeated assignment is a no-op once the DOM stops
    // thrashing.
    // Track height growth on EVERY frame as fallback. The anchor node
    // may survive the first commit pass but be torn down by a later
    // one, leaving frames 1+ with no anchor; without per-frame
    // re-targeting we'd miss thousands of px of late-arriving content.
    let frames = 0
    let lastScrollHeight = prevScrollHeight
    let lastAppliedScrollTop = viewport.scrollTop
    const restore = () => {
      const node = anchorDomId
        ? viewport.querySelector<HTMLElement>(`[data-message-id="${anchorDomId}"]`)
        : null
      if (node && anchorTop != null) {
        const delta = node.getBoundingClientRect().top - anchorTop
        if (Math.abs(delta) > 0.5) {
          viewport.scrollTop += delta
          lastAppliedScrollTop = viewport.scrollTop
        }
      } else {
        // Anchor was rebuilt/lost. Reconstruct scrollTop from the
        // cumulative height growth since the user's last seen position.
        const hDelta = viewport.scrollHeight - lastScrollHeight
        if (hDelta > 0) {
          viewport.scrollTop = lastAppliedScrollTop + hDelta
          lastAppliedScrollTop = viewport.scrollTop
        }
      }
      lastScrollHeight = viewport.scrollHeight
      if (++frames < 8) {
        requestAnimationFrame(restore)
      } else {
        setIsPaging(false)
      }
    }
    requestAnimationFrame(restore)
  }, [isPaging, runtime])

  const suggestions = getSuggestionsForView(t, currentView)
  const welcomeText = getWelcomeTextForView(t, currentView)

  return (
    <HistoryPaginationProvider value={{ hasMore, isFetchingMore, fetchMore, isPaging }}>
      <AssistantRuntimeProvider runtime={runtime}>
        <ThreadStatusContext.Provider
          value={{
            status: visibleStatus,
            dismiss,
            startNewThread: onRequestNewThread,
            tokensUsed: health?.tokens_used,
            budgetTokens: health?.budget_tokens,
          }}
        >
          <AgentOptionsContext.Provider value={agentOptionsContextValue}>
            <AgentSuggestionsContext.Provider value={{ suggestions, welcomeText }}>
              {scope === 'case' && (
                <>
                  <RunCypherToolUI />
                  <QueryStructuredTableToolUI />
                  <SearchStructuredEvidenceToolUI />
                  <CiteStructuredRowsToolUI />
                  <OpenCaseEvidenceToolUI />
                  <FindEntityByNameToolUI />
                  <FindShortestPathToolUI />
                  <HybridSearchToolUI />
                  <GetNodeNeighborhoodToolUI />
                  <GetNodeDetailsToolUI />
                  <DescribeGraphSchemaToolUI />
                  <ShowEntitiesAtLocationToolUI />
                  <FindEntitiesNearLocationToolUI />
                  <DeepResearchToolUI />
                  <EntityResolutionToolUI />
                  <PlanEntityResolutionMergeToolUI />
                  <MergeEntityResolutionToolUI />
                  <GetEntityResolutionMergeStatusToolUI />
                  <SchemaProposalToolUI />
                  <ReadUserGuidePageToolUI />
                  <SearchUserGuideToolUI />
                </>
              )}
              {scope === 'lake' && (
                <>
                  <LakeHybridSearchToolUI />
                  <LakeSearchDocumentsByNameToolUI />
                  <LakeSearchFoldersByNameToolUI />
                  <ReadUserGuidePageToolUI />
                  <SearchUserGuideToolUI />
                </>
              )}
              {children}
            </AgentSuggestionsContext.Provider>
          </AgentOptionsContext.Provider>
        </ThreadStatusContext.Provider>
      </AssistantRuntimeProvider>
    </HistoryPaginationProvider>
  )
}
